/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Drawer, Typography, useMediaQuery } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import PublishedPdfViewer from './PublishedPdfViewer';
import { useNavigate, useParams } from 'react-router-dom';
import { InfoButton, Loader } from 'components';
import { dayjs } from 'util/shared/date';
import localeData from 'dayjs/plugin/localeData';
import ReportContentWrapper from 'components/shared/ReportContentWrapper';
import ReportsList from '../../components/shared/ReportsList';
import DescriptionIcon from '@mui/icons-material/Description';
import DownloadIcon from '@mui/icons-material/Download';
import { AdminContentCategory } from 'types';
import useReports from '../../hooks/foundersNotes/useReports';
import { ReportsIndexData, ReportsData } from 'types/flowPatrol';
import { FoundersNote } from 'components/foundersNotes/FoundersNote';
import useToast from 'hooks/useToast';
import { timezoneState, userOpeningPrintEnabledState } from 'states';
import { useRecoilValue } from 'recoil';
import useContentSearch from 'hooks/useContentSearch';
import { contentIdOf, matchedTerms, searchHitToReportItem } from 'util/search';

const DocsPerPage = 15;

const SGReports = () => {
  dayjs.extend(localeData);
  const { id: selectedDocKey } = useParams();
  const toast = useToast();

  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const openingPrintEnabled = useRecoilValue(userOpeningPrintEnabledState);

  const defaultCategories = openingPrintEnabled
    ? [
        AdminContentCategory.FlowPatrol,
        AdminContentCategory.OpeningSetup,
        AdminContentCategory.SundayNotes,
      ]
    : [AdminContentCategory.FlowPatrol, AdminContentCategory.SundayNotes];

  const [selectedCategories, setSelectedCategories] =
    useState<AdminContentCategory[]>(defaultCategories);

  const currentTimezone = useRecoilValue(timezoneState);
  // selectedCategories, not availableCategories: it already includes Opening
  // Setup for beta users, and it means search respects the category filter the
  // user set on the page.
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    sort: searchSort,
    setSort: setSearchSort,
    hits: searchHits,
    loading: searchLoading,
    error: searchError,
    isSearching,
    page: searchPage,
    hasNextPage: searchHasNextPage,
    nextPage: nextSearchPage,
    prevPage: previousSearchPage,
  } = useContentSearch(selectedCategories);

  const searchItems = useMemo(
    () => searchHits.map((hit) => searchHitToReportItem(hit, currentTimezone)),
    [searchHits, currentTimezone],
  );

  // From the fragments the cluster returned, not from the raw query: the query
  // is what the reader typed, the fragments are what actually matched once
  // synonyms and stemming had been applied. Leaving search empties this, so a
  // document opened by browsing afterwards carries no marks.
  const highlightTerms = useMemo(
    () =>
      isSearching
        ? matchedTerms(
            searchHits.find((hit) => contentIdOf(hit.key) === selectedDocKey)
              ?.snippets ?? [],
          )
        : [],
    [isSearching, searchHits, selectedDocKey],
  );
  const [page, setPage] = useState(1);
  const [showingDrawer, setShowingDrawer] = useState(!isMobile);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month());
  const [selectedYear, setSelectedYear] = useState(dayjs().year());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const docRef = useRef<HTMLDivElement>(null);

  const { getReportsIndex, getReportsWithId } = useReports();

  // Query for FlowPatrol documents
  const {
    data: flowPatrolResult,
    isLoading: loadingFlowPatrol,
    error: flowPatrolError,
    refetch: refetchFlowPatrol,
  } = useQuery<ReportsIndexData>({
    queryKey: ['flowPatrolIndex', page, selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        const response = await getReportsIndex(
          AdminContentCategory.FlowPatrol,
          page,
          DocsPerPage,
          selectedMonth,
          selectedYear,
        );
        if (response.error != null) {
          throw new Error('Unable to load FlowPatrol documents');
        }
        return response;
      } catch (error) {
        console.error('Error fetching FlowPatrol documents:', error);
        throw new Error('Unable to load FlowPatrol documents');
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
  });

  // Query for Opening Setup documents - only if the Opening Print GK is on
  const {
    data: openingSetupResult,
    isLoading: loadingOpeningSetup,
    error: openingSetupError,
    refetch: refetchOpeningSetup,
  } = useQuery<ReportsIndexData>({
    queryKey: ['openingSetupIndex', page, selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        const response = await getReportsIndex(
          AdminContentCategory.OpeningSetup,
          page,
          DocsPerPage,
          selectedMonth,
          selectedYear,
        );
        if (response.error != null) {
          throw new Error('Unable to load Opening Setup documents');
        }
        return response;
      } catch (error) {
        console.error('Error fetching Opening Setup documents:', error);
        throw new Error('Unable to load Opening Setup documents');
      }
    },
    refetchOnWindowFocus: true,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
    enabled: openingPrintEnabled,
  });

  // Query for Sunday Notes documents
  const {
    data: sundayNotesResult,
    isLoading: loadingSundayNotes,
    error: sundayNotesError,
    refetch: refetchSundayNotes,
  } = useQuery<ReportsIndexData>({
    queryKey: ['sundayNotesIndex', page, selectedMonth, selectedYear],
    queryFn: async () => {
      try {
        const response = await getReportsIndex(
          AdminContentCategory.SundayNotes,
          page,
          DocsPerPage,
          selectedMonth,
          selectedYear,
        );
        if (response.error != null) {
          throw new Error('Unable to load Sunday Notes documents');
        }
        return response;
      } catch (error) {
        console.error('Error fetching Sunday Notes documents:', error);
        throw new Error('Unable to load Sunday Notes documents');
      }
    },
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
  });

  // Combine and filter documents based on selected categories
  const combinedDocs = useMemo(() => {
    // Wait for all required fetches to complete before combining
    const flowPatrolNeeded = selectedCategories.includes(
      AdminContentCategory.FlowPatrol,
    );
    const openingSetupNeeded =
      openingPrintEnabled &&
      selectedCategories.includes(AdminContentCategory.OpeningSetup);
    const sundayNotesNeeded = selectedCategories.includes(
      AdminContentCategory.SundayNotes,
    );

    // If FlowPatrol is needed but still loading, return empty array
    if (flowPatrolNeeded && loadingFlowPatrol) {
      return [];
    }

    // If Opening Setup is needed but still loading, return empty array
    if (openingSetupNeeded && loadingOpeningSetup) {
      return [];
    }

    // If Sunday Notes is needed but still loading, return empty array
    if (sundayNotesNeeded && loadingSundayNotes) {
      return [];
    }

    const allDocs: ReportsData[] = [];

    if (flowPatrolNeeded && flowPatrolResult?.data) {
      allDocs.push(...flowPatrolResult.data);
    }

    if (openingSetupNeeded && openingSetupResult?.data) {
      allDocs.push(...openingSetupResult.data);
    }

    if (sundayNotesNeeded && sundayNotesResult?.data) {
      allDocs.push(...sundayNotesResult.data);
    }

    // Sort by date descending (newest first), with null dates at the end
    return allDocs.sort((a, b) => {
      if (a.date == null && b.date == null) {
        return 0;
      }
      if (a.date == null) {
        return 1;
      }
      if (b.date == null) {
        return -1;
      }
      return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
    });
  }, [
    selectedCategories,
    flowPatrolResult?.data,
    openingSetupResult?.data,
    sundayNotesResult?.data,
    openingPrintEnabled,
    loadingFlowPatrol,
    loadingOpeningSetup,
    loadingSundayNotes,
  ]);

  const hasNextPage =
    (selectedCategories.includes(AdminContentCategory.FlowPatrol) &&
      flowPatrolResult?.hasNextPage) ||
    (openingPrintEnabled &&
      selectedCategories.includes(AdminContentCategory.OpeningSetup) &&
      openingSetupResult?.hasNextPage) ||
    (selectedCategories.includes(AdminContentCategory.SundayNotes) &&
      sundayNotesResult?.hasNextPage) ||
    false;

  const getDocByKey = useCallback(
    (docKey: string, docs: ReportsData[]) => {
      return docs.find(
        (doc) =>
          doc.key === `${AdminContentCategory.FlowPatrol}/${docKey}` ||
          doc.key === `${AdminContentCategory.SundayNotes}/${docKey}` ||
          (openingPrintEnabled &&
            doc.key === `${AdminContentCategory.OpeningSetup}/${docKey}`),
      );
    },
    [openingPrintEnabled],
  );

  // Tried in order when a link arrives with no list to resolve it against.
  const candidateCategories = useMemo(
    () =>
      openingPrintEnabled
        ? [
            AdminContentCategory.FlowPatrol,
            AdminContentCategory.OpeningSetup,
            AdminContentCategory.SundayNotes,
          ]
        : [AdminContentCategory.FlowPatrol, AdminContentCategory.SundayNotes],
    [openingPrintEnabled],
  );

  // Which '<category>/<id>' the route's bare id refers to, resolved outside the
  // query so the cache key can carry it. The browse list only holds the
  // selected month's page, so a hit from any other month is not in it; the
  // hits themselves carry the same prefixed key and category the fetch needs.
  const resolvedTarget = useMemo(() => {
    if (selectedDocKey == null) {
      return null;
    }
    const doc = getDocByKey(selectedDocKey, combinedDocs);
    if (doc != null) {
      return { key: doc.key, category: doc.category };
    }
    const hit = searchHits.find((h) => contentIdOf(h.key) === selectedDocKey);
    return hit == null ? null : { key: hit.key, category: hit.category };
  }, [selectedDocKey, combinedDocs, searchHits, getDocByKey]);

  // Query for individual document - only when we have a selected doc key
  const {
    data: selectedDoc,
    isLoading: loadingSelectedDoc,
    error: selectedDocError,
    refetch: refetchSelectedDoc,
  } = useQuery<ReportsData | null>({
    // The resolved key and the access flag are part of the identity, not just
    // the route id: a document opened before the lists arrived resolves to
    // nothing and probes for its category, and keying on the id alone would
    // cache that guess against the id forever. Only the resolved key enters
    // the key, so ordinary searchHits churn does not refetch a document that
    // still resolves the same way.
    queryKey: [
      'reportDoc',
      selectedDocKey,
      resolvedTarget?.key ?? null,
      openingPrintEnabled,
    ],
    queryFn: async () => {
      if (selectedDocKey == null) {
        return null;
      }
      try {
        if (resolvedTarget != null) {
          const response = await getReportsWithId(
            resolvedTarget.key,
            resolvedTarget.category,
          );

          if (response.data == null) {
            throw new Error('Document not found');
          }
          return response.data;
        }

        // Neither list holds it: a shared or reloaded link, where the browse
        // page has only the current month and no search has run. The route
        // carries no category, so ask each one the reader can see and keep the
        // first that answers.
        let probeFailed = false;
        for (const category of candidateCategories) {
          const response = await getReportsWithId(
            `${category}/${selectedDocKey}`,
            category,
          );
          if (response.data != null) {
            return response.data;
          }
          // fetchAPIWithLog resolves on an HTTP error rather than throwing, so
          // a failed probe is only visible in the response. A 404 is the
          // expected answer from the categories the document does not live in;
          // anything else means the probe never got to look, and reporting
          // that as 'no such document' would hide an outage behind an empty
          // panel. Remembered rather than thrown, so a later category can
          // still answer.
          if (response.error != null && Number(response.status) !== 404) {
            probeFailed = true;
          }
        }
        if (probeFailed) {
          throw new Error('Document lookup failed');
        }
        return null;
      } catch (error) {
        console.error('Error fetching document:', error);
        throw new Error('Unable to load document');
      }
    },
    refetchOnWindowFocus: true,
    // The category probe needs no list, so a key on its own is enough to run.
    enabled: selectedDocKey != null,
    staleTime: 5 * 60 * 1_000, // 5 minutes
    gcTime: 10 * 60 * 1_000, // 10 minutes
    retry: 2,
  });

  const loadSelectedDoc = useCallback(
    (docKey: string) => {
      if (isMobile) {
        setShowingDrawer(false);
      }
      const cleanKey = contentIdOf(docKey);
      navigate(`/reports/${encodeURIComponent(cleanKey)}`, { replace: true });
    },
    [isMobile, navigate],
  );

  useEffect(() => {
    if (selectedDocKey == null && combinedDocs.length > 0) {
      loadSelectedDoc(combinedDocs[0].key); // initialize with first document
    }
  }, [selectedDocKey, combinedDocs, loadSelectedDoc]);

  const retryFetch = () => {
    if (
      flowPatrolError ||
      sundayNotesError ||
      (openingPrintEnabled && openingSetupError)
    ) {
      void refetchFlowPatrol();
      void refetchSundayNotes();
      if (openingPrintEnabled) {
        void refetchOpeningSetup();
      }
    } else if (selectedDocError) {
      void refetchSelectedDoc();
    }
  };

  const refreshSidebar = async () => {
    try {
      setIsRefreshing(true);
      const promises = [refetchFlowPatrol(), refetchSundayNotes()];
      if (openingPrintEnabled) {
        promises.push(refetchOpeningSetup());
      }
      await Promise.all(promises);
    } catch (error) {
      console.error('Error refreshing sidebar:', error);
      toast.openToast({
        message: 'Failed to refresh documents. Please try again.',
        type: 'error',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const nextPage = () => {
    if (!hasNextPage) {
      return;
    }
    setPage(page + 1);
  };

  const previousPage = () => {
    if (page <= 1) {
      return;
    }
    setPage(page - 1);
  };

  const toggleShowingDrawer = () => {
    setShowingDrawer(!showingDrawer);
  };

  const handleCategoriesChange = (categories: AdminContentCategory[]) => {
    // Filter out Opening Setup if the Opening Print GK is off
    const filteredCategories = openingPrintEnabled
      ? categories
      : categories.filter((cat) => cat !== AdminContentCategory.OpeningSetup);

    setSelectedCategories(filteredCategories);
    setPage(1);
    // Reset selected doc if it's not in the new category selection
    if (selectedDocKey && filteredCategories.length > 0) {
      const isSelectedDocInCategories = filteredCategories.some((category) => {
        if (category === AdminContentCategory.FlowPatrol) {
          return selectedDocKey.startsWith(
            `${AdminContentCategory.FlowPatrol}/`,
          );
        }
        if (
          openingPrintEnabled &&
          category === AdminContentCategory.OpeningSetup
        ) {
          return selectedDocKey.startsWith(
            `${AdminContentCategory.OpeningSetup}/`,
          );
        }
        if (category === AdminContentCategory.SundayNotes) {
          return selectedDocKey.startsWith(
            `${AdminContentCategory.SundayNotes}/`,
          );
        }
        return false;
      });

      if (!isSelectedDocInCategories) {
        loadSelectedDoc(combinedDocs[0].key);
      }
    }
  };

  const downloadPdf = async () => {
    if (!selectedDoc?.pdfUrl || !selectedDocKey) {
      toast.openToast({
        message: 'PDF is not available for download',
        type: 'error',
      });
      return;
    }

    setIsDownloading(true);

    try {
      const pdfUrl = await getPdfUrlForDownload();
      const response = await fetch(pdfUrl);

      if (!response.ok) {
        handleDownloadError(response.status);
        return;
      }

      const blob = await response.blob();
      const filename = generateFilename();

      downloadBlob(blob, filename);

      toast.openToast({
        message: 'PDF downloaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      handleDownloadError();
    } finally {
      setIsDownloading(false);
    }
  };

  const getPdfUrlForDownload = async (): Promise<string> => {
    let pdfUrl = selectedDoc?.pdfUrl;
    if (!pdfUrl || !selectedDocKey) {
      throw new Error('No PDF URL available');
    }

    try {
      const response = await fetch(pdfUrl);

      // If the PDF URL has expired, get a fresh one
      if (
        !response.ok &&
        (response.status === 403 || response.status === 404)
      ) {
        toast.openToast({
          message: 'PDF link expired, fetching a fresh download link...',
          type: 'info',
        });

        const category = selectedDocKey?.startsWith(
          `${AdminContentCategory.SundayNotes}/`,
        )
          ? AdminContentCategory.SundayNotes
          : selectedDocKey?.startsWith(`${AdminContentCategory.OpeningSetup}/`)
            ? AdminContentCategory.OpeningSetup
            : AdminContentCategory.FlowPatrol;

        const freshDocResponse = await getReportsWithId(
          selectedDocKey,
          category,
        );

        if (!freshDocResponse.data?.pdfUrl) {
          throw new Error('Unable to get fresh PDF URL');
        }

        pdfUrl = freshDocResponse.data.pdfUrl;
        void refetchSelectedDoc(); // Update the query cache
      }

      return pdfUrl;
    } catch (error) {
      console.error('Error getting PDF URL:', error);
      throw new Error('Unable to access PDF');
    }
  };

  const generateFilename = (): string => {
    try {
      const baseName = selectedDoc?.title || selectedDoc?.key;
      if (!baseName) {
        return 'document.pdf';
      }
      return `${baseName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
    } catch (error) {
      console.error('Error generating filename:', error);
      return 'document.pdf';
    }
  };

  const downloadBlob = (blob: Blob, filename: string) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      throw new Error('Unable to download file');
    }
  };

  const handleDownloadError = (status?: number | null) => {
    let message: string;

    if (status === 403) {
      message =
        'Access denied. You do not have permission to download this PDF.';
    } else if (status === 404) {
      message =
        'The PDF file is no longer available. Please refresh the page and try again.';
    } else {
      message = 'Failed to download PDF. Please try again later.';
    }

    toast.openToast({
      message,
      type: 'error',
    });
  };

  const availableCategories = [
    { value: AdminContentCategory.FlowPatrol, label: 'FlowPatrol' },
    ...(openingPrintEnabled
      ? [{ value: AdminContentCategory.OpeningSetup, label: 'Opening Setup' }]
      : []),
    { value: AdminContentCategory.SundayNotes, label: 'Sunday Notes' },
  ];

  const docsListComponent = showingDrawer && (
    <ReportsList
      items={isSearching ? searchItems : combinedDocs}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      searchMode={isSearching}
      searchError={searchError}
      sortBy={searchSort}
      onSortByChange={setSearchSort}
      selectedItemKey={selectedDoc?.key ?? null}
      categories={availableCategories}
      selectedCategories={selectedCategories}
      onCategoriesChange={handleCategoriesChange}
      IconComponent={DescriptionIcon}
      onItemSelect={(key: string) => {
        if (isMobile) {
          setShowingDrawer(false);
        }
        loadSelectedDoc(key);
      }}
      onRefresh={refreshSidebar}
      onClose={toggleShowingDrawer}
      selectedMonth={selectedMonth}
      selectedYear={selectedYear}
      onMonthChange={(month: number) => {
        setSelectedMonth(month);
        setPage(1);
      }}
      onYearChange={(year: number) => {
        const newYear = year;
        if (newYear === dayjs().year() && selectedMonth > dayjs().month()) {
          setSelectedMonth(dayjs().month());
        }
        setSelectedYear(newYear);
        setPage(1);
      }}
      loading={
        isSearching
          ? searchLoading
          : loadingFlowPatrol ||
            loadingOpeningSetup ||
            loadingSundayNotes ||
            isRefreshing
      }
      currentPage={isSearching ? searchPage : page}
      hasNextPage={isSearching ? searchHasNextPage : hasNextPage}
      onNextPage={isSearching ? nextSearchPage : nextPage}
      onPreviousPage={isSearching ? previousSearchPage : previousPage}
      showPagination={true}
      paginationCondition={true}
      firstNewItemIndex={0}
    />
  );

  const sidebar = isMobile ? (
    <Drawer
      variant="temporary"
      anchor="right"
      open={showingDrawer}
      onClose={() => setShowingDrawer(false)}
    >
      {docsListComponent}
    </Drawer>
  ) : (
    docsListComponent
  );

  const isLoading =
    loadingFlowPatrol ||
    loadingOpeningSetup ||
    loadingSundayNotes ||
    loadingSelectedDoc;
  const hasError =
    flowPatrolError ||
    openingSetupError ||
    sundayNotesError ||
    selectedDocError;
  const hasNoDocs = combinedDocs.length === 0;

  const loadingOrErrored = isLoading ? (
    <Loader isLoading={isLoading} />
  ) : hasError ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: 4,
      }}
    >
      <Typography variant="h6" color="text.secondary">
        Unable to load documents
      </Typography>
      <Button variant="outlined" onClick={retryFetch}>
        Try Again
      </Button>
    </Box>
  ) : hasNoDocs ? (
    <Typography variant="h6" color="text.secondary">
      There are no documents available for this month/year.
    </Typography>
  ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        justifyContent: 'space-between',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      <Box
        sx={{
          paddingRight: 5,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '12px',
            height: '30px',
            whiteSpace: 'nowrap',
          }}
          onClick={toggleShowingDrawer}
        >
          {showingDrawer ? 'Hide all documents' : 'View all documents'}
        </Button>
        {selectedDoc?.pdfUrl && (
          <Button
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontSize: '12px',
              height: '30px',
              whiteSpace: 'nowrap',
              display: isMobile ? 'flex' : 'none',
            }}
            onClick={() => void downloadPdf()}
            startIcon={<DownloadIcon />}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download PDF'}
          </Button>
        )}
        <Box
          sx={{
            justifySelf: 'flex-end',
            gap: '4px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <InfoButton key="flow-patrol-info" articleKey="flow-patrol-info" />
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: {
            xs: '100%',
            lg: 'calc(100vh - 170px)',
          },
          justifyContent: 'space-between',
          flexDirection: 'row',
        }}
      >
        <ReportContentWrapper
          ref={docRef}
          sx={{ position: 'relative', paddingX: 4, paddingY: 0 }}
        >
          {selectedDoc?.pdfUrl && (
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                display: isMobile ? 'none' : 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  fontSize: '12px',
                  height: '30px',
                  whiteSpace: 'nowrap',
                }}
                onClick={() => void downloadPdf()}
                startIcon={<DownloadIcon />}
                disabled={isDownloading}
              >
                {isDownloading ? 'Downloading...' : 'Download PDF'}
              </Button>
            </Box>
          )}
          {loadingOrErrored ??
            (selectedDoc?.pdfUrl != null ? (
              <PublishedPdfViewer
                pdfUrl={selectedDoc.pdfUrl}
                mode="all-pages"
                highlightTerms={highlightTerms}
              />
            ) : selectedDoc?.html != null ? (
              <Box style={{ overflow: 'auto' }}>
                <FoundersNote
                  html={selectedDoc && selectedDoc.html ? selectedDoc.html : ''}
                  title={selectedDoc?.title}
                  id={selectedDoc?.key}
                  date={dayjs(selectedDoc?.date)}
                  fromWordpress={false}
                  highlightTerms={highlightTerms}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  Select a document to view
                </Typography>
              </Box>
            ))}
        </ReportContentWrapper>
        {sidebar}
      </Box>
    </Box>
  );
};

export default SGReports;
